/**
 * Pure logic behind the donor's redemption-code screens (phase5-design.md §g b).
 *
 * Everything here is a function of its inputs: no fetch, no React, no clock
 * reads. `now` is always a parameter, so a test can pin the moment and a page
 * cannot drift into deciding eligibility differently on every render.
 *
 * The server stays the authority on every rule below. These predicates exist so
 * the UI can grey out an action that would only earn a 409 — they never decide
 * what is allowed; `routes/donorCodes.ts` does, against the same settings.
 */

/**
 * The code states, mirrored from the API's `redemption_code.state` column
 * (`docs/phase5-design.md` §c). Restated here because the web app has no
 * schema import and a typo'd state string should be a type error.
 */
export type CodeState =
  | 'ISSUED'
  | 'NOTICE'
  | 'CLAIMED'
  | 'QUEUED'
  | 'REDEEMED'
  | 'EXPIRED'
  | 'CANCELLED';

export const CODE_STATES: readonly CodeState[] = [
  'ISSUED',
  'NOTICE',
  'CLAIMED',
  'QUEUED',
  'REDEEMED',
  'EXPIRED',
  'CANCELLED',
];

// ---------------------------------------------------------------------------
// Issue preview
// ---------------------------------------------------------------------------

export interface IssuePreviewInput {
  /** Seats carried by one code. */
  seats: number;
  /** How many codes to mint in this batch. */
  count: number;
  /**
   * The price of one seat, in cents, as the server reported it.
   *
   * Display-only: the browser never *sends* an amount — the server prices the
   * issue from its own settings at the moment it mints, exactly as the checkout
   * lets the server decide what a donation means. A stale price here can only
   * make the preview disagree with the response, never the ledger.
   */
  seatPriceCents: number;
}

export interface IssuePreview {
  /** Codes in the batch (seats × count passes through unchanged). */
  totalCodes: number;
  /** Seats the batch carries in total. */
  totalSeats: number;
  /** What the batch should cost, in cents. Display-only — see above. */
  totalCents: number;
}

/**
 * The seats × count math the issue screen previews before the donor confirms.
 *
 * Integer-only on purpose: every input is a count or an integer cent amount,
 * so there is no float anywhere near money.
 */
export function previewIssue(input: IssuePreviewInput): IssuePreview {
  const seats = Math.trunc(input.seats);
  const count = Math.trunc(input.count);
  return {
    totalCodes: count,
    totalSeats: seats * count,
    totalCents: seats * count * Math.trunc(input.seatPriceCents),
  };
}

// ---------------------------------------------------------------------------
// Masked hint
// ---------------------------------------------------------------------------

/**
 * The hint the codes list shows in place of the token: first 4 and last 2
 * characters with a mask between (`ABCD••YZ`).
 *
 * The token itself was shown exactly once at issue and is never stored — only
 * its SHA-256 hash is — so a token-derived hint cannot be shown later. The hint
 * is therefore derived from the **hash**, which the issue screen shows beside
 * the token so the donor can match their saved codes to these rows later. Six
 * characters of a SHA-256 hex digest of a 256-bit secret reveal nothing usable.
 *
 * Values too short to hide anything (fewer than 7 characters) are masked
 * entirely rather than half-revealed.
 */
export function maskedHint(value: string): string {
  if (value.length < 7) {
    return '•'.repeat(Math.max(value.length, 3));
  }
  return `${value.slice(0, 4)}••${value.slice(-2)}`;
}

// ---------------------------------------------------------------------------
// Extend eligibility (one extension per code)
// ---------------------------------------------------------------------------

/** Milliseconds per day — the unit the code-lifetime settings speak. */
const DAY_MS = 24 * 60 * 60 * 1000;

export interface ExtendEligibilityInput {
  state: CodeState;
  /** The code's current expiry, as the server returned it (ISO string or Date). */
  expiresAt: string | Date;
  /** The moment the question is asked, so tests can pin the clock. */
  now: string | Date;
  /**
   * How long after expiry an extension is still accepted, in days.
   *
   * The server derives this from `notice_period_days`: the donor had that many
   * days of warning before expiry, so the same window after expiry is the
   * grace. It is passed in rather than hardcoded because the setting is
   * server-owned and the UI must not guess it.
   */
  graceDays: number;
  /** True once the code has already been extended (one extension per code). */
  alreadyExtended: boolean;
}

/**
 * Whether the donor's "extend once" action can apply to this code.
 *
 * Per the owner's decision (phase5-design.md §g b): from NOTICE, or from
 * EXPIRED while still inside the grace window. A code the platform is already
 * working on (CLAIMED/QUEUED), a code that finished (REDEEMED), a terminal
 * CANCELLED code, and an already-extended code are all ineligible.
 */
export function canExtendCode(input: ExtendEligibilityInput): boolean {
  if (input.alreadyExtended) {
    return false;
  }
  if (input.state === 'NOTICE') {
    return true;
  }
  if (input.state !== 'EXPIRED') {
    return false;
  }
  const expiry = new Date(input.expiresAt).getTime();
  const now = new Date(input.now).getTime();
  if (Number.isNaN(expiry) || Number.isNaN(now)) {
    // An unparseable date is a server bug the donor cannot fix; refuse rather
    // than guess.
    return false;
  }
  return now <= expiry + input.graceDays * DAY_MS;
}

/**
 * Whether the expiry-notice banner should show for a code.
 *
 * The lifecycle job moves a code into NOTICE inside the warning window; until
 * that pass runs, the row's own `noticeAt` timestamp is the earlier signal.
 * Either fact is worth telling the donor about, so the banner shows on the
 * state or on the timestamp — never on a hardcoded countdown.
 */
export function isNoticeDue(input: {
  state: CodeState;
  noticeAt: string | null;
  now: string | Date;
}): boolean {
  if (input.state === 'NOTICE' || input.state === 'EXPIRED') {
    return true;
  }
  if (input.state !== 'ISSUED' || input.noticeAt === null) {
    return false;
  }
  const notice = new Date(input.noticeAt).getTime();
  const now = new Date(input.now).getTime();
  if (Number.isNaN(notice) || Number.isNaN(now)) {
    return false;
  }
  return notice <= now;
}

// ---------------------------------------------------------------------------
// Ask-the-platform eligibility
// ---------------------------------------------------------------------------

/**
 * Whether the donor can flag this code for the admin distribution queue
 * ("this code could not be delivered; find a student").
 *
 * A code the platform already holds (CLAIMED/QUEUED), one already redeemed, or
 * one already cancelled is not a delivery failure the queue can fix. ISSUED
 * and EXPIRED leave the state unchanged — the ask is a flag — while NOTICE
 * moves to QUEUED, per the owner's decision (§g b).
 */
export function canAskPlatform(state: CodeState): boolean {
  return state === 'ISSUED' || state === 'NOTICE' || state === 'EXPIRED';
}

// ---------------------------------------------------------------------------
// The one-time token export
// ---------------------------------------------------------------------------

export interface IssuedCodeRow {
  token: string;
  seats: number;
}

/**
 * Build the CSV the issue screen offers for download, in the order issued.
 *
 * Tokens are quoted per RFC 4180 even though a base64url token can never
 * contain a quote or separator — the quoting costs nothing and the format stays
 * correct if a row ever grows another field. Line endings are CRLF for the same
 * reason: it is what the RFC names, and spreadsheet apps agree with it.
 */
export function buildCodesCsv(rows: readonly IssuedCodeRow[]): string {
  const cell = (value: string): string => `"${value.replaceAll('"', '""')}"`;
  const lines = rows.map((row) => `${cell(row.token)},${cell(String(row.seats))}`);
  return ['token,seats', ...lines].join('\r\n');
}
